import React, { useMemo } from 'react';

/**
 * GenerationProgress
 * Shows real-time step-by-step progress during AI reviewer and quiz creation.
 * Replaces the static spinner with transparent progress tracking.
 *
 * Steps Enum:
 * 0: Reading & Extracting Content
 * 1: Validating Quality & Formatting
 * 2: AI Analyzing & Batch Processing
 * 3: Synthesizing & Generating Questions
 * 4: Finalizing Reviewer
 */
const GenerationProgress = ({ loadingText = "Processing...", step = 0, config = {} }) => {
  const steps = useMemo(() => {
    const list = [
      {
        id: 0,
        title: "Extracting lesson material",
        desc: "Parsing text and document contents",
        icon: "fa-regular fa-file-lines",
      },
      {
        id: 1,
        title: "Validating content quality",
        desc: "Checking structure and readability",
        icon: "fa-solid fa-shield-check",
      },
      {
        id: 2,
        title: "AI Analysis & Prompt Formulation",
        desc: "Structuring content for deep learning evaluation",
        icon: "fa-solid fa-brain",
      },
    ];

    if (config?.quizEnabled !== false) {
      list.push({
        id: 3,
        title: `Generating ${config?.questionCount || 10} quiz items`,
        desc: `Crafting multi-step questions & options (${config?.difficulty === 3 ? "Hard mode" : "Standard"})`,
        icon: "fa-solid fa-clipboard-question",
      });
    } else if (config?.flashcardsEnabled !== false) {
      list.push({
        id: 3,
        title: `Building ${config?.flashcardCount || 10} flashcards`,
        desc: "Extracting key definitions and core concepts",
        icon: "fa-solid fa-layer-group",
      });
    } else {
      list.push({
        id: 3,
        title: "Synthesizing master summary",
        desc: "Distilling core points and takeaways",
        icon: "fa-solid fa-align-left",
      });
    }

    list.push({
      id: 4,
      title: "Finalizing & assembling reviewer",
      desc: "Formatting interactive widgets and answers",
      icon: "fa-solid fa-sparkles",
    });

    return list;
  }, [config]);

  const progressPercent = Math.min(100, Math.round(((step + 0.5) / steps.length) * 100));

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-md transition-all duration-300 animate-fadeIn"
      role="dialog"
      aria-modal="true"
      aria-label="Generation progress"
    >
      <div className="bg-white/95 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl max-w-lg w-full p-6 sm:p-8 overflow-hidden relative">
        {/* Top Glow Accent */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500" />

        {/* Header with animated brain & badge */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-blue-600 to-indigo-500 text-white flex items-center justify-center text-xl shadow-md shadow-blue-500/20 relative">
              <i className="fa-solid fa-wand-magic-sparkles animate-pulse"></i>
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500"></span>
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 leading-tight">Generating Reviewer</h2>
              <p className="text-xs text-gray-500 font-medium">ReviewMate AI Pipeline Active</p>
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
              {progressPercent}% Complete
            </span>
          </div>
        </div>

        {/* Dynamic Progress Bar */}
        <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden mb-6 p-0.5 border border-gray-200/50">
          <div 
            className="h-full bg-gradient-to-r from-blue-600 to-indigo-600 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progressPercent}%` }}
          />
        </div>

        {/* Pipeline Step List */}
        <div className="space-y-3.5 mb-6">
          {steps.map((s) => {
            const isDone = step > s.id;
            const isCurrent = step === s.id;

            return (
              <div 
                key={s.id}
                className={`flex items-start gap-3.5 p-3 rounded-2xl transition-all duration-300 ${
                  isCurrent 
                    ? "bg-blue-50/80 border border-blue-200/70 shadow-sm" 
                    : isDone 
                      ? "bg-gray-50/60 opacity-80" 
                      : "opacity-40"
                }`}
              >
                <div className="mt-0.5">
                  {isDone ? (
                    <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs shadow-sm shadow-emerald-500/30">
                      <i className="fa-solid fa-check"></i>
                    </div>
                  ) : isCurrent ? (
                    <div className="w-6 h-6 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs shadow-md shadow-blue-500/40 animate-pulse">
                      <i className="fa-solid fa-circle-notch fa-spin"></i>
                    </div>
                  ) : (
                    <div className="w-6 h-6 rounded-full border-2 border-gray-300 flex items-center justify-center text-[10px] text-gray-400">
                      <i className={s.icon}></i>
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`text-sm font-bold truncate ${isCurrent ? "text-blue-950" : isDone ? "text-gray-700" : "text-gray-500"}`}>
                      {s.title}
                    </p>
                    {isCurrent && (
                      <span className="text-[10px] font-bold text-blue-600 tracking-wider uppercase bg-blue-100/60 px-2 py-0.5 rounded-md">
                        Active
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-1">{s.desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Live Status Message Card */}
        <div className="bg-slate-900 text-slate-100 rounded-2xl p-3.5 flex items-center gap-3 text-xs font-mono shadow-inner border border-slate-800">
          <span className="flex h-2 w-2 relative shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          <span className="truncate flex-1 font-sans text-slate-200">
            {loadingText || "Processing lesson content..."}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GenerationProgress;
